import { ApolloError, ApolloQueryResult, ExecutionResult } from 'apollo-boost';
import { useCallback } from 'react';
import { removeConnections } from '../../apollo';
import {
  ApiResponse,
  requestStart,
  requestSuccess,
  requestError,
  useRequestState,
  RequestState,
} from './useRequestState';
import { refreshTokenOnDemand } from '../auth';

const getErrorMessage = (error: ApolloError) => {
  if (!error.message || error.message.includes('401')) {
    return 'Your session has expired. Please try refreshing the page.';
  }
  return error.message;
};

const getErrorResponse = (error: ApolloError) => {
  return requestError({
    isAxiosError: false,
    name: 'GraphQL Error',
    message: error.message,
    stack: error.stack,
    response: {
      status: 500,
      data: {
        message: getErrorMessage(error),
      },
    },
  });
};

export const useApiRequest = <T, U = unknown>(
  currentState?: RequestState<T>,
) => {
  const [requestState, setRequestState] = useRequestState<T>(currentState);
  const makeRequest = useCallback(
    async (request: Promise<ApiResponse<T>>, extra?: U) => {
      setRequestState(requestStart());
      const response = await request;
      if (response.success) {
        setRequestState(requestSuccess<T, U>(response.data!, extra));
      } else {
        setRequestState(requestError(response.error!));
      }
    },
    //eslint-disable-next-line
    [setRequestState],
  );
  return [requestState, makeRequest, setRequestState] as const;
};

export const useGraphQLRequest = <T, U = undefined>(
  currentState?: RequestState<T>,
  mutation?: boolean,
) => {
  const [requestState, setRequestState] = useRequestState<T>(currentState);
  const makeRequest = useCallback(
    async (
      request: Promise<ApolloQueryResult<T> | ExecutionResult<T>>,
      extra?: U,
      retried = false,
    ) => {
      setRequestState(requestStart());
      try {
        request
          .then(response => {
            if (mutation && response.data) {
              setRequestState(requestSuccess<T, U>(response.data, extra));
              return;
            }
            setRequestState(
              requestSuccess<T, U>(removeConnections(response.data!), extra),
            );
          })
          .catch(async (error: ApolloError) => {
            if (
              !retried &&
              error.message.includes('401') &&
              (await refreshTokenOnDemand())
            ) {
              await makeRequest(request, extra, true);
              return;
            }
            setRequestState(getErrorResponse(error));
          });
      } catch (e) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setRequestState(getErrorResponse(e as any));
      }
    },
    //eslint-disable-next-line
    [setRequestState],
  );
  return [requestState, makeRequest, setRequestState] as const;
};
